import {
  createRovoBridgeAPIClient,
  type RovoContentBridgeAPI,
  type ClientId,
} from '@canvas/rovo-content-bridge-api';

import {
  IsPreviewPanelAvailableCommand,
  type IsPreviewPanelAvailableCommandProperties,
} from './commands/is-preview-panel-available';
import {
  OpenPreviewPanelCommand,
  type OpenPreviewPanelCommandProperties,
} from './commands/open-preview-panel';
import { CONFLUENCE_AFM_CLIENT_ID, CONFLUENCE_DATABASE_CLIENT_ID } from './types';

interface PreviewPanelAFMBridgeInput {
  clientId: ClientId;
  bridge: Pick<RovoContentBridgeAPI, 'send' | 'on' | 'off' | 'teardown'>;
}

export interface PreviewPanelAFMBridge {
  openPreviewPanel: (
    props: Pick<OpenPreviewPanelCommandProperties, 'url' | 'ari'>,
  ) => void;
  isPreviewPanelAvailable: (
    props: Pick<IsPreviewPanelAvailableCommandProperties, 'ari'>,
  ) => Promise<boolean | undefined>;
}

const PREVIEW_PANEL_AVAILABLE_TIMEOUT = 300;

class PreviewPanelAFMBridgeImpl implements PreviewPanelAFMBridge {
  public constructor(private input: PreviewPanelAFMBridgeInput) {
    // Ensure we unregister all command handlers when the page is unloaded to prevent
    // memory leaks when navigating
    const clientId = this.input.clientId;
    const cleanupBridge = () => {
      this.input.bridge.teardown(clientId);

      window.removeEventListener('beforeunload', cleanupBridge);
      window.removeEventListener('unload', cleanupBridge);
      window.removeEventListener('pagehide', cleanupBridge);
    };
    window.addEventListener('beforeunload', cleanupBridge, { once: true });
    window.addEventListener('unload', cleanupBridge, { once: true });
    window.addEventListener('pagehide', cleanupBridge, { once: true });
  }

  public openPreviewPanel(
    props: Pick<OpenPreviewPanelCommandProperties, 'url' | 'ari'>,
  ): void {
    this.input.bridge.send(OpenPreviewPanelCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      url: props.url,
      ari: props.ari,
    });
  }

  public async isPreviewPanelAvailable(
    props: Pick<IsPreviewPanelAvailableCommandProperties, 'ari'>,
  ): Promise<boolean | undefined> {
    const commandResult = await this.input.bridge.send(IsPreviewPanelAvailableCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      ari: props.ari,
    });

    let timeoutId: ReturnType<typeof setTimeout>;
    let timeoutWon = false;

    const timeoutPromise = new Promise<boolean>((resolve) => {
      timeoutId = setTimeout(() => {
        timeoutWon = true;
        resolve(false);
      }, PREVIEW_PANEL_AVAILABLE_TIMEOUT);
    });
    const resultPromise = commandResult.result().then((result) => {
      clearTimeout(timeoutId);
      return result ?? false;
    });

    const result = await Promise.race([resultPromise, timeoutPromise]);

    if (timeoutWon) {
      commandResult.cancel();
    }

    return result;
  }
}

export class NoOpPreviewPanelAFMBridge implements PreviewPanelAFMBridge {
  public openPreviewPanel(
    _props: Pick<OpenPreviewPanelCommandProperties, 'url' | 'ari'>,
  ): void {}

  // eslint-disable-next-line require-await
  public async isPreviewPanelAvailable(
    _props: Pick<IsPreviewPanelAvailableCommandProperties, 'ari'>,
  ): Promise<boolean> {
    return false;
  }
}

export function createPreviewPanelAFMBridge(): PreviewPanelAFMBridge {
  return new PreviewPanelAFMBridgeImpl({
    clientId: CONFLUENCE_DATABASE_CLIENT_ID,
    bridge: createRovoBridgeAPIClient(CONFLUENCE_DATABASE_CLIENT_ID, () => {}),
  });
}
